/* eslint-disable @typescript-eslint/unbound-method */
import { Component, DestroyRef, HostListener, inject, Input, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BehaviorSubject, distinctUntilChanged, Observable, Subject, tap } from 'rxjs';
import {
    IndividualizationControlName,
    IndividualizationDialogModel,
    individualizationDialogResults,
    MaterialNumberDescription,
} from './individualization-material-serial.model';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { isDefined, MatSerNumber } from '@evorait/pace-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { FormsState } from '@evorait/pace-forms';
import { getTopicEquipmentSets, getTopicMaterialNumbers } from '../../store/selectors/form.selector';
import { map, withLatestFrom } from 'rxjs/operators';

interface MaterialDescriptionInterface {
    materialDescription: string;
    materialNumber: string;
}

@Component({
    selector: 'pace-forms-individualization-material-serial',
    templateUrl: './individualization-material-serial.component.html',
    styleUrls: ['./individualization-material-serial.component.scss'],
})
export class IndividualizationMaterialSerialComponent implements OnInit {
    @Input()
    public contentId: number;

    public topicMaterialNumbers$: Observable<string[]>;
    public topicEquipmentSets$: Observable<MatSerNumber[]>;
    public filteredSerialNumber$: Observable<MatSerNumber[]>;
    public filteredMaterialDescription$: Observable<MaterialDescriptionInterface[]>;
    public chosenMaterialDescription$: Observable<string>;

    public filterSerialNumberSub = new BehaviorSubject('');
    public onMaterialNumberInputSub = new BehaviorSubject('');

    public model: IndividualizationDialogModel;
    public modalForm: FormGroup;
    public showMaterialDropdown = false;
    public showSerialDropdown = false;
    public modalResult = new Subject<individualizationDialogResults>();
    private destroy = inject(DestroyRef);
    public constructor(
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        private readonly translate: TranslateService,
        private readonly store: Store<FormsState>,
    ) {
        this.modalForm = this.formBuilder.group({
            [IndividualizationControlName.MATERIAL_NUMBER]: ['', Validators.required],
            [IndividualizationControlName.SERIAL_NUMBER]: ['', Validators.required],
            [IndividualizationControlName.DESCRIPTION]: [''],
        });

        this.model = new IndividualizationDialogModel(
            <string>this.translate.instant('INDIVIDUALIZATION_HEADER'),
            <string>this.translate.instant('INDIVIDUALIZATION_MATERIAL_NUMBER'),
            <string>this.translate.instant('INDIVIDUALIZATION_SERIAL_NUMBER'),
            <string>this.translate.instant('INDIVIDUALIZATION_DESCRIPTION'),
            <string>this.translate.instant('INDIVIDUALIZATION_SAVE'),
            <string>this.translate.instant('INDIVIDUALIZATION_CANCEL'),
        );
    }

    public ngOnInit(): void {
        const serialNumberControl: FormControl = this.modalForm.get(
            IndividualizationControlName.SERIAL_NUMBER,
        ) as FormControl;

        const materialNumberControl: FormControl = this.modalForm.get(
            IndividualizationControlName.MATERIAL_NUMBER,
        ) as FormControl;

        this.topicMaterialNumbers$ = this.store.select(getTopicMaterialNumbers({ id: this.contentId }));
        this.topicEquipmentSets$ = this.store.select(getTopicEquipmentSets({ id: this.contentId }));

        this.filteredSerialNumber$ = this.filterSerialNumberSub.asObservable().pipe(
            withLatestFrom(this.topicEquipmentSets$),
            map(([filterMaterialNumber, topicEquipmentsSet]) =>
                topicEquipmentsSet.filter((equipSet) => equipSet.matnr === filterMaterialNumber),
            ),
            tap((filteredTopicEquipmentSets) => {
                if (filteredTopicEquipmentSets.length === 1) {
                    serialNumberControl?.setValue(filteredTopicEquipmentSets[0].sernr);
                } else {
                    serialNumberControl?.setValue('');
                }
            }),
        );

        this.filteredMaterialDescription$ = this.onMaterialNumberInputSub.pipe(
            withLatestFrom(this.topicMaterialNumbers$, this.topicEquipmentSets$),
            map(([value, topicMaterialNumbers, topicEquipmentSets]) => {
                const materialNumberDescription: MaterialDescriptionInterface[] = [];
                topicMaterialNumbers.forEach((matNum) => {
                    const equipSet = topicEquipmentSets.find((equipment) => equipment.matnr === matNum);

                    if (value.length === 0 || matNum.indexOf(value) > -1) {
                        materialNumberDescription.push({
                            materialDescription: equipSet?.description ? `${matNum}-${equipSet.description}` : matNum,
                            materialNumber: matNum,
                        });
                    }
                });

                return materialNumberDescription;
            }),
        );
        this.chosenMaterialDescription$ = materialNumberControl.valueChanges.pipe(
            withLatestFrom(this.filteredMaterialDescription$),
            map(([value, filterMaterialDescription]) => {
                const foundMaterial = filterMaterialDescription.find(
                    (materialDescription) => materialDescription.materialNumber === value,
                );

                return isDefined(foundMaterial) ? foundMaterial?.materialDescription : '';
            }),
        );

        materialNumberControl?.valueChanges
            .pipe(
                distinctUntilChanged(),
                tap((materialNumber) => {
                    this.showMaterialDropdown = false;
                    this.filterSerialNumberSub.next(materialNumber);
                }),
                takeUntilDestroyed(this.destroy),
            )
            .subscribe();
    }

    @HostListener('document:click', ['$event'])
    public onDocumentClick(event: MouseEvent): void {
        if (!(event.target as Element).closest('.individualization-dropdown')) {
            this.showMaterialDropdown = false;
            this.showSerialDropdown = false;
        }
    }

    public selectMaterialNumber(controlValue: MaterialNumberDescription): void {
        this.modalForm.get(IndividualizationControlName.MATERIAL_NUMBER)?.setValue(controlValue.materialNumber);
        this.showMaterialDropdown = false;
    }

    public selectSerialNumber(controlValue: string): void {
        this.modalForm.get(IndividualizationControlName.SERIAL_NUMBER)?.setValue(controlValue);
        this.showSerialDropdown = false;
    }

    public onSerialNumberInputFocus(): void {
        this.showSerialDropdown = true;
        this.showMaterialDropdown = false;
    }

    public onMaterialNumberInput(): void {
        const enteredMaterialValue: string = this.modalForm.get(IndividualizationControlName.MATERIAL_NUMBER)?.value;
        this.onMaterialNumberInputSub.next(enteredMaterialValue);

        this.showMaterialDropdown = true;
        this.showSerialDropdown = false;
    }

    public onSubmit(): void {
        const results: individualizationDialogResults = this.modalForm.value;

        this.modalResult.next(results);
        this.modalResult.complete();
        this.modalInstance.hide();
    }

    public onCancel(): void {
        this.modalResult.complete();
        this.modalInstance.hide();
    }
}
